/**
 * Shared Access Form Component
 * 
 * Form for creating or editing a shared access (secondary user).
 * Includes account permissions selection.
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSharedAccessStore } from '../../store';
import { Modal, Button, Input } from '../ui';
import type {
  SharedAccess,
  AccountPermission,
  AccountPermissionLevel,
  BankAccount,
  CreateSharedAccessRequest,
  UpdateSharedAccessRequest,
} from '@budgira/shared';

interface SharedAccessFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingAccess: SharedAccess | null;
  accounts: BankAccount[];
}

const SharedAccessForm = ({
  isOpen,
  onClose,
  onSuccess,
  editingAccess,
  accounts,
}: SharedAccessFormProps) => {
  const { t } = useTranslation();
  const { createSharedAccess, updateSharedAccess, isLoading, error, clearError } = useSharedAccessStore();
  const errorRef = useRef<HTMLDivElement>(null);
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [canManageCategories, setCanManageCategories] = useState(false);
  const [canManagePaymentMethods, setCanManagePaymentMethods] = useState(false);
  const [accountPermissions, setAccountPermissions] = useState<AccountPermission[]>([]);
  const [sendInvitation, setSendInvitation] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  
  // Scroll to error when it appears
  useEffect(() => {
    if ((formError || error || emailError) && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [formError, error, emailError]);
  
  // Check if error is email-related (for displaying near email field)
  const isEmailRelatedError = (err: string | null): boolean => {
    if (!err) return false;
    const emailErrorPatterns = [
      'email',
      'e-mail',
      'adresse',
      'compte budgira',
      'active budgira',
      'propriétaire',
      'owner',
      'already exists',
      'existe déjà',
    ];
    return emailErrorPatterns.some(pattern => 
      err.toLowerCase().includes(pattern.toLowerCase())
    );
  };
  
  // Reset form when modal opens/closes or editingAccess changes
  useEffect(() => {
    if (isOpen) {
      clearError();
      setFormError(null);
      setEmailError(null);
      
      if (editingAccess) {
        setFirstName(editingAccess.firstName);
        setLastName(editingAccess.lastName);
        setEmail(editingAccess.email);
        setCanManageCategories(editingAccess.canManageCategories);
        setCanManagePaymentMethods(editingAccess.canManagePaymentMethods);
        setAccountPermissions(editingAccess.accountPermissions);
      } else {
        setFirstName('');
        setLastName('');
        setEmail('');
        setCanManageCategories(false);
        setCanManagePaymentMethods(false);
        // Initialize all accounts with 'none' permission
        setAccountPermissions(
          accounts.map(account => ({
            accountId: account.id,
            permission: 'none' as AccountPermissionLevel,
          }))
        );
        setSendInvitation(true);
      }
    }
  }, [isOpen, editingAccess, accounts, clearError]);
  
  // Handle store error - check if it's email-related
  useEffect(() => {
    if (error && isEmailRelatedError(error)) {
      setEmailError(error);
      clearError(); // Clear the global error since we're showing it locally
    }
  }, [error, clearError]);
  
  // Update permissions for a specific account
  const handlePermissionChange = (accountId: string, permission: AccountPermissionLevel) => {
    setAccountPermissions(prev =>
      prev.map(ap =>
        ap.accountId === accountId ? { ...ap, permission } : ap
      )
    );
  };
  
  // Validate form
  const validateForm = (): boolean => {
    if (!firstName.trim()) {
      setFormError(t('settings.sharedAccess.form.errors.firstNameRequired'));
      return false;
    }
    if (!lastName.trim()) {
      setFormError(t('settings.sharedAccess.form.errors.lastNameRequired'));
      return false;
    }
    if (!editingAccess && !email.trim()) {
      setFormError(t('settings.sharedAccess.form.errors.emailRequired'));
      return false;
    }
    if (!editingAccess && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError(t('settings.sharedAccess.form.errors.emailInvalid'));
      return false;
    }
    
    // Check at least one account has read or write permission
    const hasAnyPermission = accountPermissions.some(ap => ap.permission !== 'none');
    if (!hasAnyPermission) {
      setFormError(t('settings.sharedAccess.form.errors.noPermissions'));
      return false;
    }
    
    return true;
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setEmailError(null);
    
    if (!validateForm()) {
      return;
    }
    
    if (editingAccess) {
      // Update existing access
      const updateData: UpdateSharedAccessRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        canManageCategories,
        canManagePaymentMethods,
        accountPermissions,
      };
      
      const result = await updateSharedAccess(editingAccess.id, updateData);
      if (result) {
        onSuccess();
      }
    } else {
      // Create new access
      const createData: CreateSharedAccessRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        canManageCategories,
        canManagePaymentMethods,
        accountPermissions,
      };
      
      const result = await createSharedAccess(createData, sendInvitation);
      if (result) {
        onSuccess();
      }
    }
  };
  
  // Get permission for an account
  const getPermission = (accountId: string): AccountPermissionLevel => {
    const ap = accountPermissions.find(p => p.accountId === accountId);
    return ap?.permission || 'none';
  };
  
  // Radio button for permission selection
  const PermissionRadio = ({
    accountId,
    value,
    label,
  }: {
    accountId: string;
    value: AccountPermissionLevel;
    label: string;
  }) => {
    const isSelected = getPermission(accountId) === value;
    return (
      <label className="flex items-center cursor-pointer">
        <input
          type="radio"
          name={`permission-${accountId}`}
          value={value}
          checked={isSelected}
          onChange={() => handlePermissionChange(accountId, value)}
          className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 focus:ring-violet-500 dark:bg-slate-700"
        />
        <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">{label}</span>
      </label>
    );
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingAccess
        ? t('settings.sharedAccess.form.editTitle')
        : t('settings.sharedAccess.form.addTitle')
      }
      size="lg"
      closeOnBackdropClick={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading
              ? t('common.saving')
              : editingAccess
                ? t('common.save')
                : t('settings.sharedAccess.form.create')
            }
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General error messages (non-email related) */}
        {(formError || (error && !isEmailRelatedError(error))) && (
          <div ref={errorRef} className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
            {formError || error}
          </div>
        )}
        
        {/* Personal information */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label={t('settings.sharedAccess.form.firstName')}
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            disabled={isLoading}
            required
          />
          <Input
            label={t('settings.sharedAccess.form.lastName')}
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            disabled={isLoading}
            required
          />
        </div>
        
        {/* Email (only for new access) */}
        {!editingAccess && (
          <div>
            <Input
              label={t('settings.sharedAccess.form.email')}
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setEmailError(null); // Clear email error when user types
              }}
              disabled={isLoading}
              required
              helperText={!emailError ? t('settings.sharedAccess.form.emailHelper') : undefined}
              error={emailError || undefined}
            />
            {/* Email-specific error displayed right below the field */}
            {emailError && (
              <div ref={errorRef} className="mt-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                {emailError}
              </div>
            )}
          </div>
        )}
        
        {/* Email display (for editing) */}
        {editingAccess && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
              {t('settings.sharedAccess.form.email')}
            </label>
            <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-300">
              {editingAccess.email}
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {t('settings.sharedAccess.form.emailReadonly')}
            </p>
          </div>
        )}
        
        {/* Global permissions */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            {t('settings.sharedAccess.form.globalPermissions')}
          </label>
          
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={canManageCategories}
              onChange={e => setCanManageCategories(e.target.checked)}
              disabled={isLoading}
              className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 rounded focus:ring-violet-500 dark:bg-slate-700"
            />
            <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
              {t('settings.sharedAccess.form.canManageCategories')}
            </span>
          </label>
          
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={canManagePaymentMethods}
              onChange={e => setCanManagePaymentMethods(e.target.checked)}
              disabled={isLoading}
              className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 rounded focus:ring-violet-500 dark:bg-slate-700"
            />
            <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
              {t('settings.sharedAccess.form.canManagePaymentMethods')}
            </span>
          </label>
        </div>
        
        {/* Account permissions */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-3">
            {t('settings.sharedAccess.form.accountPermissions')}
          </label>
          
          {accounts.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 italic">
              {t('settings.sharedAccess.form.noAccounts')}
            </p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
              {accounts.map(account => (
                <div
                  key={account.id}
                  className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600"
                >
                  <div className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                    {account.name}
                    <span className="text-slate-500 dark:text-slate-400 font-normal ml-2">
                      ({account.bankName})
                    </span>
                  </div>
                  <div className="flex items-center gap-6">
                    <PermissionRadio
                      accountId={account.id}
                      value="none"
                      label={t('settings.sharedAccess.permission.none')}
                    />
                    <PermissionRadio
                      accountId={account.id}
                      value="read"
                      label={t('settings.sharedAccess.permission.read')}
                    />
                    <PermissionRadio
                      accountId={account.id}
                      value="write"
                      label={t('settings.sharedAccess.permission.write')}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Send invitation option (only for new access) */}
        {!editingAccess && (
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={sendInvitation}
                onChange={e => setSendInvitation(e.target.checked)}
                disabled={isLoading}
                className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 rounded focus:ring-violet-500 dark:bg-slate-700"
              />
              <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
                {t('settings.sharedAccess.form.sendInvitation')}
              </span>
            </label>
            <p className="mt-1 ml-6 text-xs text-slate-500 dark:text-slate-400">
              {t('settings.sharedAccess.form.sendInvitationHelper')}
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default SharedAccessForm;
